import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Database, Eye, EyeOff, FileClock, KeyRound, Lock, ShieldCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import RandomCodeVerify from './RandomCodeVerify';

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const from = location.state?.from || '/';

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setCredentials((current) => ({
      ...current,
      [name]: name === 'username' ? value.trimStart() : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!credentials.username || !credentials.password) {
      toast.error('请输入用户名和密码');
      return;
    }
    if (!isVerified) {
      toast.error('请完成验证码验证');
      return;
    }

    setIsLoading(true);
    try {
      await login(credentials.username.trim(), credentials.password);
      toast.success('身份验证通过');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.message || '登录失败，请检查凭据');
      setIsLoading(false);
    }
  };

  return (
    <main className="login-page">
      <div className="login-page__glow" aria-hidden="true" />
      <div className="login-shell">
      <section className="login-overview" aria-label="系统简介">
        <motion.div className="login-brand" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <span className="brand-mark brand-mark--large"><KeyRound aria-hidden="true" /></span>
          <div><strong>ALM Console</strong><span>Atlassian License Manager</span></div>
        </motion.div>

        <motion.div className="login-overview__content" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.42, delay: 0.06 }}>
          <span className="login-kicker"><ShieldCheck aria-hidden="true" /> PRIVATE OPERATIONS WORKSPACE</span>
          <h1>让 Atlassian 授权管理<br />更清晰、更高效。</h1>
          <p>在一个工作台中完成许可证生成、历史检索与运行状态查看，减少重复操作，保持每条记录可追踪。</p>

          <div className="login-capabilities">
            <article><KeyRound aria-hidden="true" /><div><strong>快速生成</strong><span>集中配置产品与插件授权</span></div></article>
            <article><FileClock aria-hidden="true" /><div><strong>统一归档</strong><span>随时检索和复用历史记录</span></div></article>
            <article><Database aria-hidden="true" /><div><strong>本地持久化</strong><span>关键数据保存在自有环境</span></div></article>
          </div>
        </motion.div>

        <div className="login-runtime">
          <span className="login-runtime__mark" aria-hidden="true"><KeyRound /></span>
          <div><strong>Agent Runtime</strong><span>许可证生成所需运行环境</span></div>
          <code>JAVA 11.0.17+</code>
        </div>
      </section>

      <section className="login-access">
        <motion.div
          className="auth-panel"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.38, delay: 0.1 }}
        >
          <div className="auth-panel__mobile-brand"><span className="brand-mark"><KeyRound aria-hidden="true" /></span><strong>ALM Console</strong></div>
          <div className="auth-panel__header">
            <span className="auth-panel__eyebrow">账户登录</span>
            <h2>欢迎回来</h2>
            <p>输入操作员账号，继续访问许可证管理工作台。</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            <div className="field-stack">
              <label htmlFor="username">用户名</label>
              <input
                id="username"
                name="username"
                type="text"
                value={credentials.username}
                onChange={handleInputChange}
                disabled={isLoading}
                autoComplete="username"
                placeholder="输入操作员账号"
                required
              />
            </div>

            <div className="field-stack">
              <label htmlFor="password">密码</label>
              <div className="password-field">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  autoComplete="current-password"
                  placeholder="输入账户密码"
                  required
                />
                <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? '隐藏密码' : '显示密码'}>
                  {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                </button>
              </div>
            </div>

            <RandomCodeVerify onVerify={setIsVerified} />

            <button className="auth-submit" type="submit" disabled={isLoading}>
              <span>{isLoading ? '正在验证身份…' : '进入工作台'}</span>
              <ArrowRight aria-hidden="true" />
            </button>
          </form>

          <p className="auth-panel__notice"><Lock aria-hidden="true" />安全会话已启用，关键操作会写入活动记录。</p>
        </motion.div>
      </section>
      </div>
      <footer className="login-page__footer"><span>Atlassian License Manager</span><span>Private deployment · Build 1.0</span></footer>
    </main>
  );
};

export default LoginForm;
